import { useState, useEffect } from 'react'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

type ManualInstallType = 'ios' | 'firefox' | null

function getManualInstallType(): ManualInstallType {
  const ua = navigator.userAgent

  // iOS: iPhone, iPad, iPod — also detect iPad pretending to be Mac (iPadOS 13+)
  const isIOS = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

  if (isIOS) return 'ios'

  // Firefox on any platform (mobile is the main target, but detect broadly)
  if (/Firefox/.test(ua) && !/Seamonkey/.test(ua)) return 'firefox'

  return null
}

function getIsStandalone(): boolean {
  // iOS Safari exposes a non-standard `navigator.standalone` for installed PWAs.
  const nav = navigator as Navigator & { standalone?: boolean }
  return window.matchMedia('(display-mode: standalone)').matches || nav.standalone === true
}

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [manualInstallType] = useState<ManualInstallType>(() => getManualInstallType())
  const [isStandalone] = useState(() => getIsStandalone())

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }

    window.addEventListener('beforeinstallprompt', handler)

    return () => {
      window.removeEventListener('beforeinstallprompt', handler)
    }
  }, [])

  const hasNativePrompt = deferredPrompt !== null
  const canInstall = (hasNativePrompt || manualInstallType !== null) && !isStandalone

  const promptInstall = async () => {
    if (!deferredPrompt) return

    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice

    if (outcome === 'accepted') {
      setDeferredPrompt(null)
    }
  }

  return { canInstall, promptInstall, manualInstallType, hasNativePrompt }
}
